import { KeyRound, Mail, Pencil, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/client/components/ui/button';
import { InitialsAvatar } from '@/client/components/common/InitialsAvatar';
import { trpc } from '@/client/utils/trpc';
import type { RouterOutputs } from '@/client/utils/trpc';

type Employee = RouterOutputs['employees']['list'][number];

interface PersonDetailHeaderProps {
  person: Employee;
  onEdit: () => void;
  onDelete: () => void;
}

export function PersonDetailHeader({ person, onEdit, onDelete }: PersonDetailHeaderProps) {
  const displayName = person.full_name ?? person.email;
  const [inviteStatus, setInviteStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [inviteError, setInviteError] = useState('');
  const [resetStatus, setResetStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [resetError, setResetError] = useState('');

  const resendInvite = trpc.employees.resendInvite.useMutation({
    onSuccess: () => setInviteStatus('success'),
    onError: (err) => {
      setInviteStatus('error');
      setInviteError(err.message);
    },
  });

  const resetPassword = trpc.employees.sendPasswordReset.useMutation({
    onSuccess: () => setResetStatus('success'),
    onError: (err) => {
      setResetStatus('error');
      setResetError(err.message);
    },
  });

  useEffect(() => {
    if (inviteStatus === 'success') {
      const t = setTimeout(() => setInviteStatus('idle'), 3000);
      return () => clearTimeout(t);
    }
  }, [inviteStatus]);

  useEffect(() => {
    if (resetStatus === 'success') {
      const t = setTimeout(() => setResetStatus('idle'), 3000);
      return () => clearTimeout(t);
    }
  }, [resetStatus]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: mutation.reset() functions are stable from useMutation hook
  useEffect(() => {
    resendInvite.reset();
    setInviteStatus('idle');
    setInviteError('');
    resetPassword.reset();
    setResetStatus('idle');
    setResetError('');
  }, [person.id]);

  return (
    <div className="mb-7 flex flex-wrap items-start gap-4 sm:gap-5">
      <InitialsAvatar name={displayName} size={72} />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {person.job_title ?? '—'}
        </p>
        <h1 className="mt-1 break-words text-xl font-bold sm:text-2xl">{displayName}</h1>
        <p className="mt-1.5 text-sm text-muted-foreground">{person.email}</p>
        {!person.confirmed && inviteStatus === 'success' && (
          <p className="mt-1 text-sm text-good">Invite sent.</p>
        )}
        {!person.confirmed && inviteStatus === 'error' && (
          <p className="mt-1 text-sm text-destructive">{inviteError}</p>
        )}
        {person.confirmed && resetStatus === 'success' && (
          <p className="mt-1 text-sm text-good">Password reset email sent.</p>
        )}
        {person.confirmed && resetStatus === 'error' && (
          <p className="mt-1 text-sm text-destructive">{resetError}</p>
        )}
      </div>
      <div className="flex shrink-0 basis-full flex-wrap gap-2 sm:basis-auto">
        {!person.confirmed && (
          <Button
            variant="outline"
            size="sm"
            disabled={resendInvite.isPending || inviteStatus === 'success'}
            onClick={() => resendInvite.mutate({ id: person.id })}
          >
            <Mail className="mr-1.5 h-3.5 w-3.5" />
            {resendInvite.isPending ? 'Sending…' : 'Resend invite'}
          </Button>
        )}
        {person.confirmed && (
          <Button
            variant="outline"
            size="sm"
            disabled={resetPassword.isPending || resetStatus === 'success'}
            onClick={() =>
              resetPassword.mutate({
                id: person.id,
                redirectTo: `${window.location.origin}/reset-password`,
              })
            }
          >
            <KeyRound className="mr-1.5 h-3.5 w-3.5" />
            {resetPassword.isPending
              ? 'Sending…'
              : resetStatus === 'success'
                ? 'Sent!'
                : 'Reset password'}
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={onEdit}>
          <Pencil className="mr-1.5 h-3.5 w-3.5" />
          Edit
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="text-destructive hover:text-destructive"
          onClick={onDelete}
        >
          <Trash2 className="mr-1.5 h-3.5 w-3.5" />
          Delete
        </Button>
      </div>
    </div>
  );
}
